import { Injectable } from '@angular/core';
import { MenuService } from '../../menu/menu.service';
import { Menu } from '../../menu/menu.interfaces';
import { SEARCH_INDEX, SearchIndexEntry } from './search-index';

export interface SearchResult extends SearchIndexEntry {
  // The specific keyword that matched (for highlighting a sub-label). Empty when
  // the match was on the page name itself.
  matchedKeyword: string;
}

const MAX_RESULTS = 12;

@Injectable({ providedIn: 'root' })
export class GlobalSearchService {
  constructor(private menuService: MenuService) {}

  // Set of `${module}|${page}` keys for pages the current role can actually see.
  // Derived from MenuService.getAll() so permission logic lives in one place.
  private visibleKeys(): Set<string> {
    const keys = new Set<string>();
    const menu: Menu[] = this.menuService.getAll();

    for (const item of menu) {
      if (item.type === 'sub' && item.children) {
        const moduleName = item.state || '';
        for (const child of item.children) {
          if (child.visible !== false) {
            keys.add(`${moduleName}|${child.state}`);
          }
        }
      } else if (item.type === 'link' && item.state && item.visible !== false) {
        keys.add(`${item.state}|${item.state}`);
      }
    }

    return keys;
  }

  search(term: string): SearchResult[] {
    const q = (term || '').trim().toLowerCase();
    if (!q) {
      return [];
    }

    const visible = this.visibleKeys();
    const scored: { entry: SearchIndexEntry; matchedKeyword: string; rank: number }[] = [];

    for (const entry of SEARCH_INDEX) {
      if (!visible.has(`${entry.module}|${entry.page}`)) {
        continue;
      }

      const name = entry.name.toLowerCase();
      let rank = -1;
      let matchedKeyword = '';

      if (name.startsWith(q)) {
        rank = 0; // name prefix — best
      } else if (name.includes(q)) {
        rank = 1; // name substring
      } else {
        const kw = entry.keywords.find((k) => k.toLowerCase().includes(q));
        if (kw) {
          rank = 2; // keyword match
          matchedKeyword = kw;
        }
      }

      if (rank >= 0) {
        scored.push({ entry, matchedKeyword, rank });
      }
    }

    scored.sort((a, b) => a.rank - b.rank || a.entry.name.localeCompare(b.entry.name));

    return scored
      .slice(0, MAX_RESULTS)
      .map((s) => ({ ...s.entry, matchedKeyword: s.matchedKeyword }));
  }
}
